'use client';
import React from 'react';
import {
  deleteFromLocalStorage,
  isNullOrUndefined,
} from '@r-and-a-shared-ui/utils';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  handleOauthSignedIn,
  handleOauthSignedOut,
} from '../authProviderMethods';
import { tryGetRefreshToken, USER_ID } from '../../utils';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from 'aws-amplify/auth';
import { PPAuthContext } from './PPAuthContext';
import { PPCognitoUser } from '../../types/pp-user-attribute-types';

export interface PPAuthProviderProps {
  children?: ReactNode;
  awsExports?: ResourcesConfig;
}

export const PPAuthProvider: React.FC<PPAuthProviderProps> = ({
  children,
  awsExports,
}) => {
  if (!isNullOrUndefined(awsExports)) {
    Amplify.configure(awsExports, { ssr: true });
  }

  const config = awsExports!.Auth!;

  const [user, setUser] = useState<PPCognitoUser | undefined>(undefined);
  const [roles, setRoles] = useState<string[] | undefined>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | undefined>();
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const [idToken, setIdToken] = useState<string | undefined>();

  const resetAuthState = () => {
    setUser(undefined);
    setRoles([]);
    setRefreshToken(undefined);
    setAccessToken(undefined);
    setIdToken(undefined);
    setIsAuthenticated(false);
    deleteFromLocalStorage(USER_ID);
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      const [currentUser, session, attributes] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
        fetchUserAttributes(),
      ]);

      const access = session.tokens?.accessToken?.toString();
      const id = session.tokens?.idToken?.toString();

      setUser({
        ...attributes,
        username: currentUser.username,
        userId: currentUser.userId,
      } as PPCognitoUser);

      setRefreshToken(await tryGetRefreshToken());
      setAccessToken(access);
      setIdToken(id);
      setRoles(
        (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ??
          [],
      );

      setIsAuthenticated(!!access && !!id);
    } catch {
      resetAuthState();
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  return (
    <PPAuthContext.Provider
      value={{
        methods: {
          handleOauthSignedIn,
          handleOauthSignedOut,
        },
        userState: {
          user,
          roles,
          isAuthenticated,
          refreshToken,
          accessToken,
          idToken,
          checkAuthStatus,
        },
        config,
      }}
    >
      {children}
    </PPAuthContext.Provider>
  );
};
